<script lang="ts">
  import { GraphicBlock } from '@reuters-graphics/graphics-components';

  // Line chart component
  import MultipleLineChart from '$lib/components/MultipleLineChart/index.svelte';
  import Legend from './components/Legend.svelte';

  // Types
  import type { SmallMultiplesMultiLineChartProps } from './types';

  // Props
  let {
    data,
    categoryKey,
    childCategoryKey,
    categoriesOrder,
    xValKey,
    yValKey,
    id,
    width,
    title,
    description,
    subhead,
    notes,
    altText,
    yValueLabelDecimals,
    dateParser,
    dateFormatter,
    zeroBased,
    xTicks,
    yTicks,
    height,
    customYDomain,
    lineColours,
    chartTitles,
    getCustomXDomain,
    endValLabels,
    endDotDiameter,
    endValMargin,
    legendLabels,
    yTickFormatter,
    rollingAvg,
  }: SmallMultiplesMultiLineChartProps<Record<string, unknown>> = $props();

  // Get a list of values for `categoryKey` from the data
  let categories = categoriesOrder || [
    ...new Set(data.map((d) => d[categoryKey])),
  ];
</script>

<GraphicBlock
  {width}
  {id}
  {title}
  {description}
  {notes}
  ariaDescription={altText}
>
  <!-- Optional subhead as string or snippet -->
  <!-- {#if subhead}
    {#if typeof subhead === 'string'}
      <p>{subhead}</p>
    {:else}
      {@render subhead()}
    {/if}
  {/if} -->

  <Legend categoryKey={childCategoryKey} {lineColours} {legendLabels} />

  <!-- Wrap all line charts in `small-multiples-container` to make a grid -->
  <div class="small-multiples-container">
    <!-- Loop through each category -->
    {#each categories as categoryVal, idx}
      <!-- Filter the data by categoryVal so that we pass only the data for a particular category to LineChart -->
      {@const eachCategoryData = data.filter(
        (d) => d[categoryKey] === categoryVal
      )}
      <!-- Use `lineColours` to determine the line colour. If undefined, defaults to light grey -->
      {@const lineColour =
        lineColours?.find((c) => c[categoryKey] === categoryVal)?.colour ||
        '#ccc'}
      <!-- Since we are dealing with small multiples, we may want each line chart to have the same x domain (for example, the same date range). If `getCustomXDomain()` exists, use that to define a custom x-axis domain for each category's data. -->
      {@const customXDomain =
        getCustomXDomain ? getCustomXDomain(eachCategoryData) : undefined}

      <!-- Wrap each line chart in `child-container` and use CSS to make a grid -->
      <div class="child-container {categoryKey} {categoryVal}">
        <!-- Use `chartTitles` to show custom chart titles if it exists. Otherwise, default to showing the categoryVal -->
        <h4 class="{categoryKey} {categoryVal}">
          {#if chartTitles}
            {chartTitles.find((c) => c[categoryKey] === categoryVal).title}
          {:else}
            {categoryVal}
          {/if}
        </h4>
        <!-- Draw a line chart for each presidential term -->
        <MultipleLineChart
          data={eachCategoryData}
          {xValKey}
          {yValKey}
          categoryKey={childCategoryKey}
          {lineColours}
          parentCategoryVal={categoryVal as string}
          {dateParser}
          {dateFormatter}
          class="{categoryKey} {categoryVal} chart-{idx}"
          strokeColour={lineColour}
          endDotColour={lineColour}
          customXDomain={customXDomain as [Date, Date]}
          customXTicks={customXDomain as string[]}
          {zeroBased}
          {xTicks}
          {yTicks}
          {height}
          {customYDomain}
          {yValueLabelDecimals}
          {endValLabels}
          {endDotDiameter}
          {endValMargin}
          {yTickFormatter}
          {rollingAvg}
        />
      </div>
    {/each}
  </div>
</GraphicBlock>

<style lang="scss">
  h4 {
    text-transform: none;
  }

  .small-multiples-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;

    .child-container {
      width: 30%; /* Adjust as needed, but aim for slightly less than 1/3 to allow for spacing */
      margin-bottom: 20px; /* Space between rows */
      box-sizing: border-box; /* Include padding and border in the element's total width and height */
    }
  }

  /*  Make it responsive. Adjust the width for smaller screens */
  @media (max-width: 760px) {
    .small-multiples-container {
      .child-container {
        width: 49%;
      }
    }
  }
  @media (max-width: 430px) {
    .small-multiples-container {
      flex-direction: column;
      align-items: center;

      .child-container {
        width: 100%; /* Full width on smaller screens */
        margin-bottom: 20px; /* Space between rows */
      }
    }
  }
</style>
